import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from '../../../components/shared/toast';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import '../../../components/shared/ConfirmationModal.css';
import { apiClient } from '../../../api/apiClient';
import './NoteCaseForm.css';

const LINK_TYPE_OPTIONS = [
  { value: 'songs', label: 'Songs', icon: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  )},
  { value: 'singer', label: 'Singer', icon: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )},
  { value: 'music', label: 'Music', icon: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )},
  { value: 'lyrics', label: 'Lyrics', icon: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )},
  { value: 'recode_label', label: 'Recode Label', icon: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  )},
  { value: 'others', label: 'Others', icon: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  )}
];

function NoteCaseForm({ initialData = null, onSave, onCancel, t }) {
  const todayStr = new Date().toISOString().split('T')[0];

  const [type, setType] = useState(initialData?.type || 'case');
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [priority, setPriority] = useState(
    initialData?.priority ? String(initialData.priority).toLowerCase() : 'medium'
  );
  const [status, setStatus] = useState(
    initialData?.status !== undefined ? (initialData.status == 1 || initialData.status === '1' || initialData.status === 'open' ? 1 : 0) : 1
  );
  const [startDate, setStartDate] = useState(initialData?.startDate || initialData?.start_date || todayStr);
  const [endDate, setEndDate] = useState(initialData?.endDate || initialData?.end_date || todayStr);

  // Tags chip state
  const initialTagsList = initialData?.tags ? initialData.tags.split(',').map(s => s.trim()).filter(Boolean) : ['Youtube', 'name'];
  const [tagChips, setTagChips] = useState(initialTagsList);
  const [tagInput, setTagInput] = useState('');

  // Link details state
  const [linkType, setLinkType] = useState(initialData?.linkType || initialData?.link_type || 'songs');
  
  // Multi-selection state for linked items
  const parseInitialLinks = () => {
    const raw = initialData?.linkResult || initialData?.link_result || initialData?.linkValue || '';
    if (!raw) return [];
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  };
  const [selectedLinks, setSelectedLinks] = useState(parseInitialLinks());
  const [otherTextValue, setOtherTextValue] = useState(
    (initialData?.linkType === 'others' || initialData?.link_type === 'others') ? (initialData?.linkResult || initialData?.link_result || '') : ''
  );
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dynamic Dropdown & Lazy Loading State
  const [linkOptions, setLinkOptions] = useState([]);
  const [dropdownPage, setDropdownPage] = useState(1);
  const [hasMoreOptions, setHasMoreOptions] = useState(true);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);

  const [isLinkDropdownOpen, setIsLinkDropdownOpen] = useState(false);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);

  // Situations list state
  const [situations, setSituations] = useState(initialData?.situations || initialData?.stages || []);
  
  // Situation Modal State
  const [showSituationModal, setShowSituationModal] = useState(false);
  const [editingSituationIndex, setEditingSituationIndex] = useState(null);
  const [sitStartDate, setSitStartDate] = useState(todayStr);
  const [sitEndDate, setSitEndDate] = useState(todayStr);
  const [sitDescription, setSitDescription] = useState('');
  const [sitError, setSitError] = useState('');

  // Situation Delete Modal State
  const [deleteSitIndex, setDeleteSitIndex] = useState(null);
  const [showDeleteSitModal, setShowDeleteSitModal] = useState(false);

  const dropdownRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close menus on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLinkDropdownOpen(false);
      }
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setIsSuggestionsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeLinkType = LINK_TYPE_OPTIONS.find(opt => opt.value === linkType) || LINK_TYPE_OPTIONS[0];

  // Helper to fetch backend options with pagination & status=1, is_delete=0
  const fetchLinkOptions = useCallback(async (pageNum, query, typeKey, append = false) => {
    if (typeKey === 'others') return;
    try {
      setIsFetchingOptions(true);
      let endpoint = '';
      const queryParams = new URLSearchParams({
        page: String(pageNum),
        limit: '20',
        status: '1'
      });
      if (query.trim()) {
        queryParams.append('search', query.trim());
      }

      if (typeKey === 'songs') {
        endpoint = `/songs?${queryParams.toString()}`;
      } else if (typeKey === 'singer' || typeKey === 'music' || typeKey === 'lyrics') {
        queryParams.append('role', typeKey);
        endpoint = `/artists?${queryParams.toString()}`;
      } else if (typeKey === 'recode_label') {
        endpoint = `/record-label?${queryParams.toString()}`;
      }

      if (!endpoint) return;

      const res = await apiClient(endpoint);
      if (res) {
        const rawItems = res.songs || res.artists || res.items || res.recordLabels || res.data || [];
        const mappedNames = rawItems.map(item => {
          if (typeKey === 'songs') {
            const mainName = item.name || item.title || '';
            let extra = '';
            if (item.nameSinhala && item.nameSinhala.trim()) {
              extra += ` (${item.nameSinhala.trim()})`;
            }
            const isrcVal = item.isrc || item.isrc_code || item.isrcCode;
            if (isrcVal && String(isrcVal).trim()) {
              extra += ` - ${String(isrcVal).trim()}`;
            }
            return `${mainName}${extra}`.trim();
          }
          return item.name || item.nameSinhala || item.title || '';
        }).filter(Boolean);
        
        // Remove duplicates
        const uniqueNames = Array.from(new Set(mappedNames));

        if (append) {
          setLinkOptions(prev => Array.from(new Set([...prev, ...uniqueNames])));
        } else {
          setLinkOptions(uniqueNames);
        }

        const totalPages = res.totalPages || 1;
        setHasMoreOptions(pageNum < totalPages && uniqueNames.length >= 20);
      }
    } catch (err) {
      console.error('Error loading link options:', err);
    } finally {
      setIsFetchingOptions(false);
    }
  }, []);

  // Fetch initial options when linkType changes or search query updates
  useEffect(() => {
    if (linkType !== 'others') {
      setDropdownPage(1);
      fetchLinkOptions(1, searchQuery, linkType, false);
    }
  }, [linkType, searchQuery, fetchLinkOptions]);

  // Lazy loading scroll handler for dropdown list
  const handleDropdownScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 20 && hasMoreOptions && !isFetchingOptions) {
      const nextPage = dropdownPage + 1;
      setDropdownPage(nextPage);
      fetchLinkOptions(nextPage, searchQuery, linkType, true);
    }
  };

  // Tag chip handlers
  const handleAddTag = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const val = tagInput.replace(/,/g, '').trim();
      if (val && !tagChips.includes(val)) {
        setTagChips([...tagChips, val]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTagChips(tagChips.filter(t => t !== tagToRemove));
  };

  const handleSelectLinkType = (val) => {
    setLinkType(val);
    setSelectedLinks([]);
    setOtherTextValue('');
    setSearchQuery('');
    setIsLinkDropdownOpen(false);
  };

  const handleToggleSelectLinkItem = (itemName) => {
    if (selectedLinks.includes(itemName)) {
      setSelectedLinks(selectedLinks.filter(item => item !== itemName));
    } else {
      setSelectedLinks([...selectedLinks, itemName]);
    }
    setSearchQuery('');
  };

  const handleRemoveSelectedLinkItem = (itemName) => {
    setSelectedLinks(selectedLinks.filter(item => item !== itemName));
  };

  // Open Create Situation Popup
  const handleOpenCreateSituation = () => {
    setEditingSituationIndex(null);
    setSitStartDate(todayStr);
    setSitEndDate(todayStr);
    setSitDescription('');
    setSitError('');
    setShowSituationModal(true);
  };

  // Open Edit Situation Popup
  const handleOpenEditSituation = (index) => {
    const sit = situations[index];
    setEditingSituationIndex(index);
    setSitStartDate(sit.startDate || sit.start_date || sit.date || todayStr);
    setSitEndDate(sit.endDate || sit.end_date || todayStr);
    setSitDescription(sit.description || '');
    setSitError('');
    setShowSituationModal(true);
  };

  // Save Situation in Memory with Toast Validation
  const handleSaveSituationModal = () => {
    if (!sitStartDate) {
      setSitError('Please select situation start date.');
      toast.warning('Please select situation start date.');
      return;
    }
    if (!sitEndDate) {
      setSitError('Please select situation end date.');
      toast.warning('Please select situation end date.');
      return;
    }
    if (sitEndDate < sitStartDate) {
      setSitError('Situation end date cannot be before start date.');
      toast.warning('Situation end date cannot be before start date.');
      return;
    }
    if (!sitDescription || !sitDescription.trim()) {
      setSitError('Please enter situation description.');
      toast.warning('Please enter situation description.');
      return;
    }

    setSitError('');
    const newSitObj = {
      startDate: sitStartDate,
      start_date: sitStartDate,
      endDate: sitEndDate,
      end_date: sitEndDate,
      date: sitStartDate,
      description: sitDescription.trim(),
      label: editingSituationIndex !== null 
        ? (editingSituationIndex === 0 ? 'start' : String(editingSituationIndex + 1).padStart(2, '0'))
        : (situations.length === 0 ? 'start' : String(situations.length + 1).padStart(2, '0'))
    };

    if (editingSituationIndex !== null) {
      const updated = [...situations];
      updated[editingSituationIndex] = newSitObj;
      setSituations(updated);
      toast.success('Situation updated successfully.');
    } else {
      setSituations([...situations, newSitObj]);
      toast.success('Situation added successfully.');
    }

    setShowSituationModal(false);
  };

  // Confirm Delete Situation
  const handleConfirmDeleteSituation = () => {
    if (deleteSitIndex !== null) {
      const updated = situations.filter((_, idx) => idx !== deleteSitIndex);
      const reindexed = updated.map((sit, idx) => ({
        ...sit,
        label: idx === 0 ? 'start' : String(idx + 1).padStart(2, '0')
      }));
      setSituations(reindexed);
      setDeleteSitIndex(null);
      setShowDeleteSitModal(false);
      toast.success('Situation deleted successfully.');
    }
  };

  // Form Submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!name || !name.trim()) {
      toast.warning('Please enter name.');
      return;
    }

    if (!startDate) {
      toast.warning('Please select start date.');
      return;
    }

    if (type === 'case' && endDate && startDate && endDate < startDate) {
      toast.warning('End date cannot be before start date.');
      return;
    }

    const tagsString = tagChips.length > 0 ? tagChips.join(', ') : (tagInput.trim() || 'Youtube, name');

    let finalLinkResult = '';
    if (linkType === 'others') {
      finalLinkResult = otherTextValue.trim();
    } else {
      finalLinkResult = selectedLinks.length > 0 ? selectedLinks.join(', ') : searchQuery.trim();
    }

    const payload = {
      type,
      name: name.trim(),
      description: description.trim(),
      tags: tagsString,
      priority,
      status: Number(status),
      linkType,
      link_type: linkType,
      linkResult: finalLinkResult,
      link_result: finalLinkResult,
      linkValue: finalLinkResult,
      startDate,
      start_date: startDate,
      endDate: type === 'case' ? endDate : null,
      end_date: type === 'case' ? endDate : null,
      situations: type === 'case' ? situations : [],
      stages: type === 'case' ? situations : []
    };

    onSave(payload);
  };

  return (
    <div className="add-document-wrapper">
      {/* Breadcrumb Nav */}
      <div className="breadcrumb-nav">
        <span className="breadcrumb-link" onClick={onCancel}>
          {t('notesCases.breadcrumb') || 'Note & Cases'}
        </span>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">
          {initialData ? 'Edit' : (t('notesCases.form.breadcrumbAdd') || 'Add new')}
        </span>
      </div>

      {/* Header Row */}
      <div className="add-document-header-row">
        <div className="header-icon-box">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
        <div className="header-text-container">
          <h1 className="add-document-title">
            {t('notesCases.title') || 'Note & Cases'}
          </h1>
          <p className="add-document-subtitle">
            {initialData ? 'Edit existing note or case details.' : 'Create note or cases records and link to songs and details.'}
          </p>
        </div>
      </div>

      {/* Main Form Fields Card with HTML validation disabled */}
      <form onSubmit={handleSubmit} className="form-card" noValidate>
        <div className="form-grid-2col">
          {/* Select Note or Case */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('notesCases.form.selectLabel') || 'Type'}
              <span className="required-asterisk">*</span>
            </label>
            <select
              className="form-select"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="case">Case</option>
              <option value="note">Note</option>
            </select>
          </div>

          {/* Name Input */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('notesCases.form.nameLabel') || 'Name'}
              <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              className="form-text-input"
              placeholder={t('notesCases.form.namePlaceholder') || 'Enter name'}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        {/* Description Textarea */}
        <div className="form-field-group">
          <label className="form-field-label">
            {t('notesCases.form.descriptionLabel') || 'Description'}
          </label>
          <textarea
            className="form-textarea"
            placeholder={t('notesCases.form.descriptionPlaceholder') || 'Enter description'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Tags with Removable Chips */}
        <div className="form-field-group">
          <label className="form-field-label">
            {t('notesCases.form.tagsLabel') || 'Tags (Press Enter or comma to add tags)'}
          </label>
          <div className="tags-chip-container">
            {tagChips.map(tag => (
              <span key={tag} className="tag-chip">
                {tag}
                <button
                  type="button"
                  className="tag-chip-remove"
                  onClick={() => handleRemoveTag(tag)}
                  title="Remove tag"
                >
                  &times;
                </button>
              </span>
            ))}
            <input
              type="text"
              className="tags-chip-input"
              placeholder={tagChips.length === 0 ? "Enter type or tags..." : "Add tag..."}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
            />
          </div>
        </div>

        {/* Priority Selection & Status Selection */}
        <div className="form-grid-2col">
          <div className="form-field-group">
            <label className="form-field-label">
              {t('notesCases.form.priorityLabel') || 'Priority'}
              <span className="required-asterisk">*</span>
            </label>
            <div className="priority-button-group">
              <button
                type="button"
                className={`priority-btn ${priority === 'high' ? 'active priority-high' : ''}`}
                onClick={() => setPriority('high')}
              >
                High
              </button>
              <button
                type="button"
                className={`priority-btn ${priority === 'medium' ? 'active priority-medium' : ''}`}
                onClick={() => setPriority('medium')}
              >
                Medium
              </button>
              <button
                type="button"
                className={`priority-btn ${priority === 'low' ? 'active priority-low' : ''}`}
                onClick={() => setPriority('low')}
              >
                Low
              </button>
              <button
                type="button"
                className={`priority-btn ${priority === 'neutral' ? 'active priority-neutral' : ''}`}
                onClick={() => setPriority('neutral')}
              >
                Neutral
              </button>
            </div>
          </div>

          <div className="form-field-group">
            <label className="form-field-label">
              Status
              <span className="required-asterisk">*</span>
            </label>
            <div className="priority-button-group">
              <button
                type="button"
                className={`priority-btn ${status === 1 ? 'active priority-medium' : ''}`}
                onClick={() => setStatus(1)}
                style={status === 1 ? { backgroundColor: '#0b66e3', color: '#ffffff', borderColor: '#0b66e3' } : {}}
              >
                Open
              </button>
              <button
                type="button"
                className={`priority-btn ${status === 0 ? 'active priority-high' : ''}`}
                onClick={() => setStatus(0)}
                style={status === 0 ? { backgroundColor: '#ef4444', color: '#ffffff', borderColor: '#ef4444' } : {}}
              >
                Close / Closed
              </button>
            </div>
          </div>
        </div>

        {/* Date Fields (Start Date & End Date depending on Type) */}
        <div className="form-grid-2col">
          <div className="form-field-group">
            <label className="form-field-label">
              Start Date
              <span className="required-asterisk">*</span>
            </label>
            <input
              type="date"
              className="form-text-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {type === 'case' && (
            <div className="form-field-group">
              <label className="form-field-label">
                End Date (Optional)
              </label>
              <input
                type="date"
                className="form-text-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Link Dropdown + Search Group */}
        <div className="form-field-group" style={{ position: 'relative' }}>
          <label className="form-field-label">
            {t('notesCases.form.linkLabel') || 'Link Information'}
          </label>

          <div className="link-form-row">
            {/* Type selector (e.g. Songs, Singer, Music...) */}
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="link-type-select-btn"
                onClick={() => setIsLinkDropdownOpen(!isLinkDropdownOpen)}
              >
                <span className="link-type-selected-info">
                  {activeLinkType.icon}
                  {activeLinkType.label}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Custom Popup menu */}
              {isLinkDropdownOpen && (
                <div className="link-dropdown-menu">
                  {LINK_TYPE_OPTIONS.map(opt => (
                    <div
                      key={opt.value}
                      className={`link-option-item ${linkType === opt.value ? 'active' : ''}`}
                      onClick={() => handleSelectLinkType(opt.value)}
                    >
                      {opt.icon}
                      <span>{opt.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* If linkType === 'others', render custom text input */}
            {linkType === 'others' ? (
              <div className="search-input-wrapper">
                <input
                  type="text"
                  className="form-text-input"
                  placeholder="Enter custom link information..."
                  value={otherTextValue}
                  onChange={(e) => setOtherTextValue(e.target.value)}
                />
              </div>
            ) : (
              /* Search Input Container with Multi-Selection Chips */
              <div className="search-input-wrapper" ref={suggestionsRef}>
                <div 
                  className="link-chips-input-box"
                  onClick={() => setIsSuggestionsOpen(true)}
                >
                  <span className="search-icon-inside">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                  </span>

                  {/* Render Selected Multi-Link Chips */}
                  {selectedLinks.map(item => (
                    <span key={item} className="link-item-chip">
                      {item}
                      <button
                        type="button"
                        className="link-item-chip-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSelectedLinkItem(item);
                        }}
                        title="Remove link"
                      >
                        &times;
                      </button>
                    </span>
                  ))}

                  <input
                    type="text"
                    className="link-search-field-input"
                    placeholder={selectedLinks.length === 0 ? `Search & link ${activeLinkType.label}...` : "Add more..."}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setIsSuggestionsOpen(true);
                    }}
                    onFocus={() => setIsSuggestionsOpen(true)}
                  />
                </div>

                {/* Suggestions Dropdown with Scroll Lazy Loading (Aligned 100% directly below) */}
                {isSuggestionsOpen && (
                  <div className="link-search-suggestions" onScroll={handleDropdownScroll}>
                    {linkOptions.map(optName => {
                      const isSelected = selectedLinks.includes(optName);
                      return (
                        <div
                          key={optName}
                          className={`suggestion-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleToggleSelectLinkItem(optName)}
                        >
                          <span>{optName}</span>
                          {isSelected && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      );
                    })}

                    {linkOptions.length === 0 && !isFetchingOptions && (
                      <div className="suggestion-loading-indicator">
                        No {activeLinkType.label.toLowerCase()} found matching your query.
                      </div>
                    )}

                    {isFetchingOptions && (
                      <div className="suggestion-loading-indicator">
                        Loading {activeLinkType.label.toLowerCase()}...
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Situations Management Section (Case Only) */}
        {type === 'case' && (
          <div className="form-situations-section">
            <div className="situations-header-row">
              <h3 className="situations-title">Situations Timeline</h3>
              <button
                type="button"
                className="btn-create-situation"
                onClick={handleOpenCreateSituation}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Create Situation</span>
              </button>
            </div>

            {situations.length > 0 ? (
              <div className="situations-list-container">
                {situations.map((sit, idx) => (
                  <div key={idx} className="situation-item-card">
                    <div className="situation-item-info">
                      <span className="situation-item-title">
                        Stage {idx === 0 ? '01 (start)' : String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="situation-item-dates">
                        {sit.startDate || sit.start_date || sit.date} — {sit.endDate || sit.end_date || 'Ongoing'}
                      </span>
                      <p className="situation-item-desc">{sit.description}</p>
                    </div>
                    <div className="situation-item-actions">
                      <button
                        type="button"
                        className="situation-action-btn"
                        onClick={() => handleOpenEditSituation(idx)}
                        title="Edit situation"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="situation-action-btn delete"
                        onClick={() => {
                          setDeleteSitIndex(idx);
                          setShowDeleteSitModal(true);
                        }}
                        title="Delete situation"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
                No situations added yet. Click "Create Situation" to add stage details.
              </p>
            )}
          </div>
        )}

        {/* Footer Buttons */}
        <div className="form-actions-footer">
          <button
            type="button"
            className="form-btn btn-outlined"
            onClick={onCancel}
          >
            {t('notesCases.form.cancel') || 'Cancel'}
          </button>
          <button
            type="submit"
            className="form-btn btn-primary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            <span>
              {t('notesCases.form.save') || `Save ${type === 'case' ? 'Case' : 'Note'}`}
            </span>
          </button>
        </div>
      </form>

      {/* Create / Edit Situation Popup Modal */}
      {showSituationModal && (
        <div 
          className="modal-backdrop-overlay" 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh', 
            backgroundColor: 'rgba(15, 23, 42, 0.55)', 
            backdropFilter: 'blur(8px)', 
            WebkitBackdropFilter: 'blur(8px)', 
            zIndex: 10000, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}
        >
          <div 
            className="modal-card-container" 
            style={{ 
              width: '90%', 
              maxWidth: '500px', 
              backgroundColor: '#ffffff', 
              background: '#ffffff', 
              borderRadius: '16px', 
              padding: '24px', 
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)', 
              border: '1px solid #e2e8f0', 
              position: 'relative', 
              zIndex: 10001, 
              color: '#1e293b', 
              display: 'flex', 
              flexDirection: 'column' 
            }}
            role="dialog"
            aria-modal="true"
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#0f172a' }}>
                {editingSituationIndex !== null ? 'Edit Situation' : 'Create Situation'}
              </h3>
              <button 
                type="button" 
                className="modal-close-icon-btn" 
                onClick={() => setShowSituationModal(false)}
                aria-label="Close modal"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {sitError && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '10px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{sitError}</span>
              </div>
            )}

            <div className="form-field-group" style={{ marginBottom: '16px' }}>
              <label className="form-field-label">
                Start Date <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                className="form-text-input"
                value={sitStartDate}
                onChange={(e) => setSitStartDate(e.target.value)}
              />
            </div>

            <div className="form-field-group" style={{ marginBottom: '16px' }}>
              <label className="form-field-label">
                End Date <span className="required-asterisk">*</span>
              </label>
              <input
                type="date"
                className="form-text-input"
                value={sitEndDate}
                onChange={(e) => setSitEndDate(e.target.value)}
              />
            </div>

            <div className="form-field-group" style={{ marginBottom: '24px' }}>
              <label className="form-field-label">
                Description <span className="required-asterisk">*</span>
              </label>
              <textarea
                className="form-textarea"
                placeholder="Enter situation details..."
                value={sitDescription}
                onChange={(e) => setSitDescription(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                className="form-btn btn-outlined"
                onClick={() => setShowSituationModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="form-btn btn-primary"
                onClick={handleSaveSituationModal}
              >
                Save Situation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Situation Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteSitModal}
        type="danger"
        title="Delete Situation"
        message="Are you sure you want to delete this situation from the timeline?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDeleteSituation}
        onCancel={() => {
          setShowDeleteSitModal(false);
          setDeleteSitIndex(null);
        }}
      />
    </div>
  );
}

export default NoteCaseForm;
